/**
 * Zone Statistics Utilities
 *
 * Calculate recall statistics for zones (total recalls, unique VINs)
 */

import { supabase, getProdSupabaseClient } from '@/lib/supabase/client';
import type { NormalizedGeometry } from './zoneGeometry';
import * as turf from '@turf/turf';
import type { Feature, Polygon, MultiPolygon } from 'geojson';
import { getExcludedDealers } from '@/features/recalls/services/queryBuilder';
import { getTenantIdFromSession } from '@/lib/supabase/tenantScoping';
import { getBrandsForOEM } from '@/constants/oems';

/** Response type for get_recall_count_for_zip_codes RPC */
interface RecallCountRPCResponse {
  total_recalls: number;
  unique_vins: number;
}

/**
 * Get recall count using the new RPC that counts directly in SQL.
 * This avoids the 50k row limit of viewport RPCs.
 */
export async function getRecallCountFromRPC(
  zipCodes: string[],
  oem?: string,
  tenantId?: string
): Promise<{ totalRecalls: number; uniqueVins: number } | null> {
  const prodClient = getProdSupabaseClient();

  try {
    // Use type assertion since the RPC may not be in generated types yet
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = (await (prodClient.rpc as any)('get_recall_count_for_zip_codes', {
      p_zip_codes: zipCodes,
      p_oem: oem || null,
      p_tenant_id: tenantId || null
    })) as { data: RecallCountRPCResponse[] | null; error: Error | null };

    if (error) {
      console.warn('[zoneStatistics] get_recall_count_for_zip_codes RPC error:', error);
      return null;
    }

    if (!data || data.length === 0) {
      return { totalRecalls: 0, uniqueVins: 0 };
    }

    // RPC returns a single row with total_recalls and unique_vins
    const row = data[0];
    if (!row) {
      return { totalRecalls: 0, uniqueVins: 0 };
    }
    return {
      totalRecalls: Number(row.total_recalls) || 0,
      uniqueVins: Number(row.unique_vins) || 0
    };
  } catch (err) {
    console.warn('[zoneStatistics] get_recall_count_for_zip_codes error:', err);
    return null;
  }
}

export interface ZoneStatistics {
  totalRecalls: number;
  uniqueVins: number;
  totalVehicles: number;
}

/**
 * Deduplicate cars by VIN and merge their safety_recalls arrays
 */
function deduplicateCarsByVin(
  data: Array<Record<string, unknown>>
): Array<Record<string, unknown>> {
  const uniqueCars = new Map<string, Record<string, unknown>>();
  data.forEach((car: Record<string, unknown>) => {
    const vin = (car.vin as string) || '';
    if (!vin) return;

    if (!uniqueCars.has(vin)) {
      uniqueCars.set(vin, car);
    } else {
      // Merge safety_recalls arrays if we find a duplicate VIN
      const existingCar = uniqueCars.get(vin);
      if (!existingCar) return;
      const existingRecalls = (existingCar.safety_recalls as Array<Record<string, unknown>>) || [];
      const newRecalls = (car.safety_recalls as Array<Record<string, unknown>>) || [];

      // Combine and deduplicate recalls by campaign_number
      const recallsMap = new Map<string, Record<string, unknown>>();
      [...existingRecalls, ...newRecalls].forEach((recall: Record<string, unknown>) => {
        const campaignKey = (recall.campaign_number as string) || '';
        if (campaignKey && !recallsMap.has(campaignKey)) {
          recallsMap.set(campaignKey, recall);
        }
      });

      existingCar.safety_recalls = Array.from(recallsMap.values());
    }
  });
  return Array.from(uniqueCars.values());
}

/**
 * Calculate recall statistics for a zone based on ZIP codes
 * Queries recalls that match the ZIP codes within the zone
 * If geometry is provided, also filters by point-in-polygon for accuracy
 */
/* eslint-disable max-lines-per-function */
export async function calculateZoneStatistics(
  zipCodes: string[],
  userId?: string,
  campaignPreferences?: Record<string, { mobile_eligible?: boolean; skill_level?: number }>,
  snapshotDate?: string | null,
  oem?: string
): Promise<ZoneStatistics> {
  if (!zipCodes || zipCodes.length === 0) {
    return {
      totalRecalls: 0,
      uniqueVins: 0,
      totalVehicles: 0
    };
  }

  const prodClient = getProdSupabaseClient();
  // Still resolved for the legacy fallback path below, which branches on tenant type.
  const tenantId = await getTenantIdFromSession(prodClient);

  // Try the new RPC first (no 50k limit, counts directly in SQL).
  // Deliberately NOT tenant-scoped: this figure sizes a zone, and a zone's size must not
  // depend on which tenant the admin happens to have selected. Passing the session tenant
  // is what made Pittsburgh preview 342 while the saved zone read 702. The RPC applies the
  // as-of cutoff server-side, so all four callers here get the same number the zone list
  // and zone map show once the zone is saved.
  const rpcResult = await getRecallCountFromRPC(zipCodes, oem);
  if (rpcResult !== null) {
    // New RPC succeeded - return counts directly
    // Note: excluded dealers are not applied (per design decision)
    return {
      totalRecalls: rpcResult.totalRecalls,
      uniqueVins: rpcResult.uniqueVins,
      totalVehicles: rpcResult.uniqueVins
    };
  }

  // Fallback to legacy logic if new RPC is not available
  console.warn('[zoneStatistics] Falling back to legacy 50k-limited viewport RPC');

  // Get excluded dealers if userId provided
  const excludedDealers = userId ? await getExcludedDealers(supabase, userId) : [];

  let allData: any[] = [];
  const snapshotDateParam = snapshotDate
    ? snapshotDate.includes('T')
      ? snapshotDate.split('T')[0]
      : snapshotDate
    : null;

  // Check if tenant is internal - internal tenants should use get_recalls_for_viewport (no zip filtering)
  let isInternalTenant = false;
  if (tenantId) {
    try {
      const { data: tenantData } = await prodClient
        .from('tenants')
        .select('tenant_type')
        .eq('id', tenantId)
        .maybeSingle();

      isInternalTenant = tenantData?.tenant_type === 'internal';
    } catch (err) {
      // Ignore error, continue with regular tenant logic
    }
  }

  // For internal tenants or no tenant ID, use get_recalls_for_viewport (no zip code filtering)
  if (!tenantId || isInternalTenant) {
    // For internal tenants, use RPC function that filters to latest snapshot_date per zone_slug
    // This prevents double counting of the same recalls across multiple snapshot dates
    if (isInternalTenant) {
      const { data: rpcDataFiltered, error: rpcErrorFiltered } = await prodClient.rpc(
        'get_recalls_for_viewport_latest_per_zone',
        {
          p_south_lat: null,
          p_north_lat: null,
          p_west_lng: null,
          p_east_lng: null,
          p_snapshot_date: snapshotDateParam,
          p_limit: 50000,
          p_offset: 0
        }
      );

      if (rpcErrorFiltered) {
        console.warn(
          '[zoneStatistics] get_recalls_for_viewport_latest_per_zone RPC error, falling back to get_recalls_for_viewport:',
          rpcErrorFiltered
        );
        // Fallback to old RPC if new one doesn't exist
        const { data: rpcData, error: rpcError } = await prodClient.rpc(
          'get_recalls_for_viewport',
          {
            p_south_lat: null,
            p_north_lat: null,
            p_west_lng: null,
            p_east_lng: null,
            p_snapshot_date: snapshotDateParam,
            p_limit: 50000,
            p_offset: 0
          }
        );

        if (rpcError) {
          console.error('[zoneStatistics] Error fetching recalls from viewport RPC:', rpcError);
          throw rpcError;
        }

        // Apply client-side filtering as fallback
        const { filterRecallsByLatestSnapshotPerZoneSlug } = await import(
          './filterRecallsByLatestSnapshot'
        );
        allData = await filterRecallsByLatestSnapshotPerZoneSlug(rpcData || []);
      } else {
        allData = rpcDataFiltered || [];
      }
    } else {
      // No tenant ID - use regular get_recalls_for_viewport
      const { data: rpcData, error: rpcError } = await prodClient.rpc('get_recalls_for_viewport', {
        p_south_lat: null,
        p_north_lat: null,
        p_west_lng: null,
        p_east_lng: null,
        p_snapshot_date: snapshotDateParam,
        p_limit: 50000,
        p_offset: 0
      });

      if (rpcError) {
        console.error('[zoneStatistics] Error fetching recalls from viewport RPC:', rpcError);
        throw rpcError;
      }

      allData = rpcData || [];
    }
  } else {
    // Regular tenant - use get_recalls_for_viewport with null bounds to get all data
    // Note: Regular tenants will still get zip-filtered results from the RPC (if implemented)
    // But we filter by ZIP codes client-side anyway to ensure accuracy
    const { data: rpcData, error: rpcError } = await prodClient.rpc('get_recalls_for_viewport', {
      p_south_lat: null,
      p_north_lat: null,
      p_west_lng: null,
      p_east_lng: null,
      p_snapshot_date: snapshotDateParam,
      p_limit: 50000,
      p_offset: 0
    });

    if (rpcError) {
      console.error('[zoneStatistics] Error fetching recalls from viewport RPC:', rpcError);
      throw rpcError;
    }

    allData = rpcData || [];
  }

  // Filter by ZIP codes
  const zipCodeSet = new Set(zipCodes);
  let data = allData.filter((r: any) => zipCodeSet.has(r.seller_postal_code));

  // If snapshot date was provided and not already filtered by RPC, filter by snapshot_date
  // (get_recalls_for_viewport and get_recalls_for_tenant_with_filters handle snapshot_date filtering)
  // But we double-check here for safety
  if (snapshotDateParam && data.length > 0) {
    const filteredByDate = data.filter((r: any) => {
      if (!r.snapshot_date) return false;
      const recordDate =
        r.snapshot_date instanceof Date
          ? r.snapshot_date.toISOString().split('T')[0]
          : typeof r.snapshot_date === 'string'
            ? r.snapshot_date.split('T')[0]
            : null;
      return recordDate === snapshotDateParam;
    });

    // Use filtered data if we got results, otherwise use all data
    if (filteredByDate.length > 0) {
      data = filteredByDate;
    }
  }

  // Filter by OEM brands when OEM is specified (mandatory: counts are always for selected OEM only)
  // Empty brands (e.g. "Other") => 0 recalls so counts stay OEM-scoped
  if (oem) {
    const oemBrands = getBrandsForOEM(oem);
    if (oemBrands.length > 0) {
      const oemBrandsLower = oemBrands.map(b => b.toLowerCase());
      data = data.filter((r: any) => {
        const makeName = (r.make_name || '').toLowerCase();
        return oemBrandsLower.includes(makeName);
      });
    } else {
      data = []; // No brand mapping (e.g. "Other") => 0 recalls
    }
  }

  return processZoneStatistics(data, excludedDealers, campaignPreferences, oem);
}

/**
 * Process zone statistics from filtered data
 * Counts unique (vin, campaign_number) pairs directly from safety_recalls JSONB arrays.
 * M/N campaign exclusion is applied only for Ford OEM; other OEMs include M/N campaigns.
 */
function processZoneStatistics(
  data: any[],
  excludedDealers: string[],
  campaignPreferences?: Record<string, { mobile_eligible?: boolean; skill_level?: number }>,
  oem?: string
): ZoneStatistics {
  if (!data || data.length === 0) {
    return {
      totalRecalls: 0,
      uniqueVins: 0,
      totalVehicles: 0
    };
  }

  // Filter out excluded dealers first (before processing)
  const filteredData =
    excludedDealers.length > 0
      ? data.filter((row: any) => {
          const dealerName = row.service_provider_name || row.seller_name || '';
          return !excludedDealers.includes(dealerName);
        })
      : data;

  // Only exclude M/N campaigns for Ford; other OEMs (e.g. Stellantis) include M/N
  const excludeMnCampaigns = oem === 'Ford';

  // Count unique (vin, campaign_number) pairs directly from safety_recalls JSONB arrays
  // This matches the SQL query: COUNT(DISTINCT (vin, campaign_number))
  const uniqueRecallPairs = new Set<string>();
  const uniqueVinsSet = new Set<string>();
  const vinMap = new Map<string, any>();

  filteredData.forEach((row: any) => {
    const vin = row.vin || '';
    if (!vin) return;

    uniqueVinsSet.add(vin);

    // Process safety_recalls JSONB array
    let safetyRecalls = row.safety_recalls;

    // Handle string JSON
    if (typeof safetyRecalls === 'string') {
      try {
        safetyRecalls = JSON.parse(safetyRecalls);
      } catch {
        return; // Skip this row if JSON is malformed
      }
    }

    if (!Array.isArray(safetyRecalls)) {
      return; // Skip if not an array
    }

    const validRecalls = excludeMnCampaigns
      ? safetyRecalls.filter((recall: any) => {
          const campaignNumber = recall?.campaign_number || '';
          return campaignNumber && !campaignNumber.includes('M') && !campaignNumber.includes('N');
        })
      : safetyRecalls.filter((recall: any) => {
          const campaignNumber = recall?.campaign_number || '';
          return !!campaignNumber;
        });

    // Count unique (vin, campaign_number) pairs
    validRecalls.forEach((recall: any) => {
      const campaignNumber = recall?.campaign_number || '';
      if (campaignNumber) {
        // Create unique key for (vin, campaign_number) pair
        const recallKey = `${vin}_${campaignNumber}`;
        uniqueRecallPairs.add(recallKey);
      }
    });

    // Store row data for deduplication if needed
    if (!vinMap.has(vin)) {
      vinMap.set(vin, row);
    }
  });

  const uniqueRecalls = uniqueRecallPairs.size;
  const uniqueVins = uniqueVinsSet.size;

  return {
    totalRecalls: uniqueRecalls, // Count unique (VIN, campaign_number) combinations
    uniqueVins,
    totalVehicles: uniqueVins // Vehicles = unique VINs
  };
}

/**
 * Calculate recall statistics using geometry intersection
 * More accurate but slower - tests each recall location against the polygon
 */
export async function calculateZoneStatisticsFromGeometry(
  geometry: NormalizedGeometry
): Promise<ZoneStatistics> {
  // Get bounding box for initial filter
  const bbox = turf.bbox(
    geometry.type === 'Polygon'
      ? turf.polygon(geometry.coordinates as number[][][])
      : turf.multiPolygon(geometry.coordinates as number[][][][])
  );

  // Query recalls within bounding box
  // Note: This is a simplified approach - for full accuracy, we'd need to
  // query all recalls and test point-in-polygon, which is expensive
  // For now, we'll rely on ZIP code matching which is faster

  // Create polygon feature for point-in-polygon testing
  const polygonFeature: Feature<Polygon | MultiPolygon> =
    geometry.type === 'Polygon'
      ? turf.polygon(geometry.coordinates as number[][][])
      : turf.multiPolygon(geometry.coordinates as number[][][][]);

  // Use RPC function to get tenant-scoped recalls with viewport bounds
  const { getRecallsForTenantWithFilters } = await import('@/lib/supabase/tenantScoping');
  const prodClient = getProdSupabaseClient();
  const tenantId = await getTenantIdFromSession(prodClient);

  if (!tenantId) {
    console.warn('[zoneStatistics] No tenant ID available for geometry calculation');
    return {
      totalRecalls: 0,
      uniqueVins: 0,
      totalVehicles: 0
    };
  }

  const { data, error } = await getRecallsForTenantWithFilters(
    prodClient,
    tenantId,
    bbox[1], // south
    bbox[3], // north
    bbox[0], // west
    bbox[2], // east
    10000,
    0
  );

  if (error) {
    throw error;
  }

  if (!data || data.length === 0) {
    return {
      totalRecalls: 0,
      uniqueVins: 0,
      totalVehicles: 0
    };
  }

  // Filter by point-in-polygon
  const recallsInZone = data.filter(recall => {
    if (!recall.seller_latitude || !recall.seller_longitude) {
      return false;
    }

    const point = turf.point([recall.seller_longitude, recall.seller_latitude]);
    return turf.booleanPointInPolygon(point, polygonFeature);
  });

  // Count unique VINs
  const uniqueVins = new Set(recallsInZone.map(recall => recall.vin)).size;

  return {
    totalRecalls: recallsInZone.length,
    uniqueVins,
    totalVehicles: uniqueVins
  };
}

/**
 * Get zone color based on recall count
 * Enhanced colors for better map distinction and accessibility
 */
export function getZoneColor(recallCount: number): string {
  if (recallCount < 300) return '#22c55e'; // Bright Green - Small (better contrast)
  if (recallCount < 500) return '#f97316'; // Vibrant Orange - Medium
  if (recallCount < 700) return '#dc2626'; // Strong Red - Large
  return '#9333ea'; // Rich Purple - Extra Large
}

/**
 * Get zone size label based on recall count
 */
export function getZoneSizeLabel(recallCount: number): string {
  if (recallCount < 300) return 'Small';
  if (recallCount < 500) return 'Medium';
  if (recallCount < 700) return 'Large';
  return 'Extra Large';
}

/**
 * Get dash pattern for zone boundaries based on zone characteristics
 * Helps distinguish zones when colors are similar or overlapping
 */
export function getZoneDashPattern(recallCount: number, zoneIndex: number): string | undefined {
  // Base patterns by size
  const sizePattern =
    recallCount >= 700
      ? '12, 8' // Extra Large: long dashes
      : recallCount >= 500
        ? '8, 4' // Large: medium dashes
        : recallCount >= 300
          ? '6, 3' // Medium: short dashes
          : undefined; // Small: solid line

  // Add variation for zones with similar recall counts to avoid visual conflicts
  const variation = zoneIndex % 3;
  if (sizePattern && variation > 0) {
    // Modify pattern slightly for visual distinction
    const [dash, gap] = sizePattern.split(', ').map(Number);
    if (variation === 1) return `${dash + 2}, ${gap}`; // Slightly longer dash
    if (variation === 2) return `${dash}, ${gap + 2}`; // Slightly longer gap
  }

  return sizePattern;
}

/**
 * Get border color for zones with alternating patterns for same-size neighboring zones
 */
export function getZoneBorderColor(
  isSelected: boolean,
  recallCount: number,
  zoneIndex: number = 0
): string {
  if (isSelected) return '#000000'; // Black for selected zones

  // Create subtle variation for neighboring zones of the same type
  const baseColor = getZoneColor(recallCount);
  const isAlternate = zoneIndex % 2 === 1;

  if (isAlternate) {
    // Create a darker variant for alternate zones of the same type
    const darkVariant = darkenColor(baseColor, 0.2);
    return darkVariant;
  }

  return baseColor;
}

/**
 * Darken a hex color by a given factor (0-1)
 */
function darkenColor(hex: string, factor: number): string {
  // Remove # if present
  const color = hex.replace('#', '');

  // Parse RGB components
  const r = parseInt(color.slice(0, 2), 16);
  const g = parseInt(color.slice(2, 4), 16);
  const b = parseInt(color.slice(4, 6), 16);

  // Darken each component
  const newR = Math.max(0, Math.floor(r * (1 - factor)));
  const newG = Math.max(0, Math.floor(g * (1 - factor)));
  const newB = Math.max(0, Math.floor(b * (1 - factor)));

  // Convert back to hex
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
}

/**
 * Get enhanced dash pattern based on zone index to distinguish neighboring zones
 */
export function getZoneDistinctionPattern(
  recallCount: number,
  zoneIndex: number
): string | undefined {
  // Only use patterns for zones that might be adjacent and same size
  const sizeCategory =
    recallCount >= 700 ? 'xl' : recallCount >= 500 ? 'l' : recallCount >= 300 ? 'm' : 's';

  const patternVariation = zoneIndex % 3;

  switch (sizeCategory) {
    case 's': // Small zones - solid vs dotted
      return patternVariation === 1 ? '2, 2' : undefined;

    case 'm': // Medium zones - short dash variations
      return patternVariation === 1 ? '6, 3' : patternVariation === 2 ? '4, 2' : undefined;

    case 'l': // Large zones - medium dash variations
      return patternVariation === 1 ? '8, 4' : patternVariation === 2 ? '10, 2' : '6, 6';

    case 'xl': // Extra large zones - long dash variations
      return patternVariation === 1 ? '12, 8' : patternVariation === 2 ? '16, 4' : '8, 8';

    default:
      return undefined;
  }
}
